import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

// Shared primitives. Mobile-first: every interactive element is ≥ 44 px tall (WCAG 2.5.5 target size), layouts are
// fluid (no fixed widths), long text wraps (`break-words`), and every state is announced to assistive technology.

export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ── Button ──

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'onHero';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-brand-600 text-white hover:brightness-110 disabled:bg-brand-600/50',
  secondary: 'bg-surface text-ink border border-line hover:bg-canvas disabled:text-muted',
  danger: 'bg-danger-solid text-white hover:brightness-110 disabled:opacity-50',
  ghost: 'bg-transparent text-brand-700 hover:bg-brand-50 disabled:text-muted',
  /** Solid white, on the passenger's coloured panel (both of its buttons alike: prompt20 §2). */
  onHero: 'bg-white text-hero-action hover:bg-brand-50 disabled:opacity-60',
};

type Size = 'md' | 'sm';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: Variant;
  /** `sm`: compact text for blocks of row actions — the 44 px touch height stays. */
  readonly size?: Size;
  readonly loading?: boolean;
  readonly fullWidth?: boolean;
}

const BUTTON_BASE =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl font-semibold disabled:cursor-not-allowed';
const SIZES: Record<Size, string> = { md: 'px-4 py-2 text-base', sm: 'px-3 py-1.5 text-sm' };

/** The look of a Button, for an element that must stay a link (navigation): same height, padding and colours. */
export function buttonClass(variant: Variant = 'primary', className?: string, size: Size = 'md'): string {
  return cn(BUTTON_BASE, SIZES[size], VARIANTS[variant], className);
}

/**
 * The actions of a table row (owner, 2026-10-07): one tidy vertical block on the right, every button the same width
 * and the same compact size. Use it as the content of the LAST column; no action lives in another column.
 */
export function ActionStack({ children }: { readonly children: ReactNode }) {
  return (
    <div className="ml-auto flex w-60 max-w-full flex-col items-end justify-center gap-2 [&>button]:w-full">
      {children}
    </div>
  );
}

/** A menu entry or a tab: orange when active (white text on the deep orange, 4.8:1). */
export function pillClass(active: boolean): string {
  return cn(
    'inline-flex min-h-11 items-center rounded-lg px-4 text-base font-medium',
    active ? 'bg-brand-600 text-white' : 'bg-surface text-ink hover:bg-brand-50',
  );
}

/** A plain text link with a 44 px target ("← Mis estadías", "Abrir"). */
export const TEXT_LINK =
  'inline-flex min-h-11 items-center font-medium text-brand-700 underline-offset-4 hover:underline';

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={buttonClass(variant, cn(fullWidth && 'w-full', className), size)}
    >
      {loading && <Spinner label="" />}
      {children}
    </button>
  );
}

// ── Spinner ──

export function Spinner({ label = 'Cargando…' }: { readonly label?: string }) {
  return (
    <span role={label === '' ? undefined : 'status'} className="inline-flex items-center gap-2">
      <span
        aria-hidden="true"
        className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      />
      {label !== '' && <span className="sr-only">{label}</span>}
    </span>
  );
}

// ── TextField ──

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | undefined;
}

export function TextField({ label, hint, error, className, ...rest }: TextFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint === undefined ? undefined : hintId, error === undefined ? undefined : errorId]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        {...rest}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        className={cn(
          'min-h-12 w-full rounded-xl border bg-surface px-4 py-2 text-base text-ink',
          error === undefined ? 'border-line' : 'border-danger',
        )}
      />
      {hint !== undefined && (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} className="text-sm break-words text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

// ── CheckboxField ──

export interface CheckboxFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  /** What the person is agreeing to; may contain a link. */
  readonly children: ReactNode;
}

/** A checkbox with its label as one 48 px tap target (the whole row toggles it). */
export function CheckboxField({ children, className, ...rest }: CheckboxFieldProps) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border border-line px-3 py-3',
        className,
      )}
    >
      <input id={id} type="checkbox" {...rest} className="mt-0.5 size-6 shrink-0 accent-brand-700" />
      <span className="text-base text-ink">{children}</span>
    </label>
  );
}

// ── Alert ──

type Tone = 'info' | 'success' | 'warning' | 'error';

const TONES: Record<Tone, string> = {
  info: 'bg-brand-50 text-brand-800 border-brand-100',
  success: 'bg-success-bg text-success border-success/30',
  warning: 'bg-warning-bg text-warning border-warning/30',
  error: 'bg-occupied-bg text-danger border-danger/30',
};

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  readonly tone?: Tone;
  readonly title?: string;
  readonly children?: ReactNode;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('rounded-lg border px-4 py-3 text-sm break-words', TONES[tone])}
    >
      {title !== undefined && <p className="font-semibold">{title}</p>}
      {children !== undefined && <div className={title === undefined ? '' : 'mt-1'}>{children}</div>}
    </div>
  );
}

// ── Dialog (native <dialog>: focus trap, Escape and inert background come from the platform) ──

export interface DialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
}

export function Dialog({ open, title, onClose, children, actions }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      className={cn(
        // Phone: bottom sheet using the full width; larger screens: centered card.
        'm-0 mt-auto w-full max-w-none rounded-t-2xl bg-surface p-0 text-ink shadow-xl backdrop:bg-black/60',
        'sm:m-auto sm:max-w-md sm:rounded-3xl',
      )}
    >
      <div className="flex flex-col gap-4 p-5">
        <h2 id={titleId} className="text-lg font-semibold break-words">
          {title}
        </h2>
        {children !== undefined && <div className="text-base break-words">{children}</div>}
        {actions !== undefined && (
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{actions}</div>
        )}
      </div>
    </dialog>
  );
}

// ── Toasts (polite live region) ──

interface Toast {
  readonly id: number;
  readonly tone: Tone;
  readonly text: string;
}

const ToastContext = createContext<(tone: Tone, text: string) => void>(() => undefined);

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const push = useCallback((tone: Tone, text: string) => {
    const id = nextId.current++;
    setToasts((current) => [...current.slice(-2), { id, tone, text }]);
    setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id));
    }, 5_000);
  }, []);
  // A modal <dialog> lives in the browser's top layer and makes the rest of the page inert: a message about the very
  // command the dialog sent would sit dimmed behind its backdrop. The toast region is a manual popover, re-shown on
  // every change so it is stacked above whatever dialog is open (no z-index can do that).
  const region = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = region.current;
    if (element === null || typeof element.showPopover !== 'function') return;
    if (element.matches(':popover-open')) element.hidePopover();
    if (toasts.length > 0) element.showPopover();
  }, [toasts]);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        ref={region}
        popover="manual"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 top-4 bottom-auto z-50 m-0 flex w-auto flex-col items-center gap-2 border-0 bg-transparent p-0 px-4 sm:top-auto sm:bottom-4"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn('w-full max-w-md rounded-lg border px-4 py-3 text-sm shadow-lg', TONES[toast.tone])}
          >
            {toast.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (tone: Tone, text: string) => void {
  return useContext(ToastContext);
}

/**
 * A page this account may not open, typed into the address bar (prompt20 §1): says "Acceso denegado" and renders its
 * child — the app's redirect to the account's own home. The API refuses those resources anyway (403/404): this is
 * only so the person understands why they were sent back.
 */
export function AccessDenied({ children }: { readonly children: ReactNode }) {
  const toast = useToast();
  useEffect(() => {
    toast('error', 'Acceso denegado: esa página no está disponible para tu cuenta.');
  }, [toast]);
  return <>{children}</>;
}

// ── Layout ──

export function AppShell({
  appName,
  actions,
  wide = false,
  children,
}: {
  readonly appName: string;
  /** Data-heavy apps (backoffice) use a wider content column on large screens. */
  readonly wide?: boolean;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}) {
  // "Funtour · Habitaciones": the brand in bold, the application's name in grey (design baseline).
  const [brand = appName, ...rest] = appName.split(' · ');
  const section = rest.join(' · ');
  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:m-2 focus:rounded focus:bg-surface focus:p-2"
      >
        Ir al contenido
      </a>
      <header className="sticky top-0 z-30 border-b border-line bg-surface/95 backdrop-blur">
        <div
          className={cn(
            'mx-auto flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5',
            wide ? 'max-w-7xl' : 'max-w-5xl',
          )}
        >
          <p className="flex items-center gap-3 text-base text-ink">
            <FuntourMark size={36} />
            <span>
              <b>{brand}</b>
              {section !== '' && <span className="text-muted"> · {section}</span>}
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <ThemeToggle />
            {actions}
          </div>
        </div>
      </header>
      <main
        id="contenido"
        className={cn('mx-auto w-full flex-1 px-4 py-6 sm:px-5 sm:py-8', wide ? 'max-w-7xl' : 'max-w-5xl')}
      >
        {children}
      </main>
    </div>
  );
}

/**
 * The official Funtour mark (`public/logo-funtour.png` of each application — the same file the dashboard uses). Served
 * by the application itself (no external request) and decorative: the name next to it is the text alternative.
 */
export function FuntourMark({ size = 28 }: { readonly size?: number }) {
  return <img src="/logo-funtour.png" alt="" width={size} height={size} className="shrink-0 rounded-xl" />;
}

export function PageTitle({
  children,
  subtitle,
  eyebrow,
}: {
  readonly children: ReactNode;
  readonly subtitle?: ReactNode;
  /** The small orange line above the title ("Vista hotel", "Administración"…). */
  readonly eyebrow?: string;
}) {
  return (
    <div className="mb-6">
      {eyebrow !== undefined && <p className="mb-1 text-sm font-bold text-brand-700">{eyebrow}</p>}
      <h1 className="text-3xl font-bold break-words sm:text-4xl">{children}</h1>
      {subtitle !== undefined && <p className="mt-1 text-base break-words text-muted">{subtitle}</p>}
    </div>
  );
}

export function Card({ children, className }: { readonly children: ReactNode; readonly className?: string }) {
  return (
    <section className={cn('rounded-2xl border border-line bg-surface p-5 shadow-sm', className)}>{children}</section>
  );
}

export function EmptyState({ title, children }: { readonly title: string; readonly children?: ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-line bg-surface px-4 py-10 text-center">
      <p className="text-base font-medium">{title}</p>
      {children !== undefined && <div className="mt-2 text-sm text-muted">{children}</div>}
    </div>
  );
}

// ── Connection indicator (real time is never silently stale, REALTIME_DESIGN §8) ──

const CONNECTION_TEXT: Record<'connecting' | 'live' | 'reconnecting' | 'polling', { label: string; dot: string }> = {
  connecting: { label: 'Conectando…', dot: 'bg-muted' },
  live: { label: 'En vivo', dot: 'bg-free' },
  reconnecting: { label: 'Reconectando…', dot: 'bg-warning' },
  polling: { label: 'Actualización cada 10 s', dot: 'bg-warning' },
};

export function ConnectionStatus({ state }: { readonly state: keyof typeof CONNECTION_TEXT }) {
  const { label, dot } = CONNECTION_TEXT[state];
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-muted">
      <span aria-hidden="true" className={cn('size-2.5 rounded-full', dot)} />
      {label}
    </span>
  );
}

// ── Filter chips (design baseline): one choice among a few, shown as pills ──

export function FilterChips<K extends string>({
  label,
  options,
  value,
  onChange,
}: {
  /** What is being filtered, for assistive technology ("Filtrar habitaciones"). */
  readonly label: string;
  readonly options: readonly (readonly [K, string])[];
  readonly value: K;
  readonly onChange: (value: K) => void;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map(([key, text]) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => {
            onChange(key);
          }}
          className={cn(
            'min-h-11 rounded-full border px-4 py-2 text-sm',
            // Active: Funtour orange with DARK bold text (5.4:1; white on it would be 3.3:1). Inactive: the text color.
            value === key
              ? 'border-brand-500 bg-brand-500 font-bold text-on-brand'
              : 'border-line bg-surface font-semibold text-ink hover:bg-canvas',
          )}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

// ── Stat card (design baseline): one figure with its caption ──

export function StatCard({
  caption,
  value,
  children,
}: {
  readonly caption: string;
  readonly value: string;
  readonly children?: ReactNode;
}) {
  // Every card alike (owner, 2026-10-07: no orange border on one of them).
  return (
    <div className="rounded-2xl border border-line bg-surface p-5 text-ink">
      <p className="text-sm font-semibold">{caption}</p>
      <p className="mt-2 text-3xl font-bold">{value}</p>
      {children}
    </div>
  );
}

// ── Password field with "show what I typed" (design baseline) ──

export function PasswordField({ label, hint, className, ...rest }: Omit<TextFieldProps, 'type' | 'error'>) {
  const id = useId();
  const hintId = `${id}-hint`;
  const [visible, setVisible] = useState(false);
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          {...rest}
          type={visible ? 'text' : 'password'}
          aria-describedby={hint === undefined ? undefined : hintId}
          className="min-h-12 w-full rounded-xl border border-line bg-surface py-2 pr-14 pl-4 text-base text-ink"
        />
        {/* The name avoids the field's own label, so "the field called X" still means the input alone. */}
        <button
          type="button"
          aria-label={visible ? 'Ocultar lo escrito' : 'Mostrar lo escrito'}
          aria-pressed={visible}
          onClick={() => {
            setVisible(!visible);
          }}
          className="absolute top-0 right-0 flex h-12 w-12 items-center justify-center rounded-xl text-muted hover:text-ink"
        >
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
            <circle cx="12" cy="12" r="3" />
            {visible && <path d="M4 4l16 16" />}
          </svg>
        </button>
      </div>
      {hint !== undefined && (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}

// ── Light / dark theme (owner, 2026-10-07) ──

type Theme = 'light' | 'dark';
const THEME_KEY = 'rooming.theme';

/** The theme saved in this browser, or the device's preference. Storage may be unavailable: never fatal. */
export function initialTheme(): Theme {
  try {
    const saved = window.localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // private window or blocked storage: fall back to the device
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Applies a theme to the page (called before the first render too, so the page never flashes the other one). */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme;
}

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() =>
    document.documentElement.dataset['theme'] === 'dark' ? 'dark' : 'light',
  );
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      aria-pressed={dark}
      aria-label="Modo oscuro"
      title={dark ? 'Pasar a modo claro' : 'Pasar a modo oscuro'}
      onClick={() => {
        const next: Theme = dark ? 'light' : 'dark';
        applyTheme(next);
        setTheme(next);
        try {
          window.localStorage.setItem(THEME_KEY, next);
        } catch {
          // not saved: it still applies to this page
        }
      }}
      className="inline-flex size-11 items-center justify-center rounded-full border border-line bg-surface text-ink hover:bg-canvas"
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {dark ? (
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        ) : (
          <>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </>
        )}
      </svg>
    </button>
  );
}
